import { ListboxInput, ListboxArrow, ListboxButton, ListboxList } from '@reach/listbox';
import { useField } from 'formik';
import React__default, { useCallback } from 'react';
import styled from 'styled-components';
import { setTestId } from '../../lib/testId.js';
import { COLOR_PALETTE } from '../colors/colorPalette.js';
import { InnerInputLabel } from '../inputs/InputCommon.js';
import { Stack } from '../layout/Stack.js';
import { useCheckModal } from '../modal/Modal.js';
import { Text } from '../text/Text.js';
import { CSSDefaultInputField } from './common.js';
import { useSelectedLabel, SelectPopover, SELECT_OPTION_NO_VALUE } from './DEPRECATED_commonSelect.js';
import { onChangeFieldWrapper, hasError } from './Field.js';
import { FIELD_ERROR_DATA_ATTRIBUTE, setErrorDataAttribute, FieldErrorMessage } from './FieldError.js';

/**
 * local constants
 */ const MIN_INPUT_WIDTH_REM = 13;
/**
 * Styled components
 * (custom styling applied to `@reach/listbox` - see
 * https://reacttraining.com/reach-ui/listbox#listboxinput)
 */ const StyledListboxInput = styled(ListboxInput)`
  width: 100%;
  min-width: ${MIN_INPUT_WIDTH_REM}rem;

  & [data-reach-listbox-button] {
    ${CSSDefaultInputField}
    position: relative;
    display: flex;
    cursor: pointer;
    padding: 1.5rem 2.5rem 0.5rem 1rem;
    align-items: flex-start;

    [${FIELD_ERROR_DATA_ATTRIBUTE}] > &,
    [${FIELD_ERROR_DATA_ATTRIBUTE}] > &:hover,
    [${FIELD_ERROR_DATA_ATTRIBUTE}] > &:focus {
      color: inherit;
    }
  }

  &[data-state='expanded'] [data-reach-listbox-button],
  &[data-state='expanded'] [data-reach-listbox-button]:hover,
  & [data-reach-listbox-button]:focus {
    outline: none;
    border-color: ${COLOR_PALETTE.slateGrey40};
  }
`;
const StyledListBoxArrow = styled(ListboxArrow)`
  position: absolute;
  top: 50%;
  margin-top: -2px;
  right: 1rem;
  display: block;
  width: 0px;
  height: 0px;
  content: '';
  transform: rotate(0deg);
  border-top: 5px solid ${COLOR_PALETTE.slateGrey100};
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  transition: transform 0.2s ease 0s, -webkit-transform 0.2s ease 0s;

  &[data-expanded] {
    transform: rotate(180deg);
  }
`;
const InputValueLabel = styled(Text)``;
/**
 * Helper functions
 */ /**
 * Select Input with Formik Field bindings.
 * Accepts `SelectOptions` as child components. If a 'deselected' option is required, it
 * must be manually added to options with a value of SELECT_OPTION_NO_VALUE.
 *
 * @example
 * <SelectField name="chicken">
 *  <SelectOption label="" value={SELECT_OPTION_NO_VALUE} />
 *  <SelectOption label="Egg" value="egg" />
 *  <SelectOption label="Duck" value="duck" />
 * </SelectField>
 */ const DEPRECATED_SelectField = ({ name , label , children , testId ="X" , reserveMessageSpace =true , onFieldChange , ...props })=>{
    const isModal = useCheckModal();
    const [field, meta, helpers] = useField(name);
    const { setValue , setTouched  } = helpers;
    // store current selected option data
    const selectedLabel = useSelectedLabel(children, field);
    // wrap the on change to perform side effects if needed
    const onChange = useCallback((value)=>{
        const updatedValue = value === SELECT_OPTION_NO_VALUE ? "" : value;
        !!onFieldChange ? onChangeFieldWrapper(onFieldChange, setValue)(updatedValue) : setValue(updatedValue);
    }, [
        onFieldChange,
        setValue
    ]);
    return React__default.createElement(Stack, {
        "data-hpy-web-select-field": true,
        gap: 0.25,
        ...setErrorDataAttribute(hasError(meta))
    }, React__default.createElement(StyledListboxInput, {
        "data-hpy-web-select-field-input": true,
        ...setTestId("SelectField-" + testId),
        ...props,
        value: field.value,
        portal: true,
        onChange: onChange
    }, React__default.createElement(ListboxButton, {
        ...selectedLabel ? {
            "data-hpy-web-select-input-input-has-value": true
        } : {},
        ...setTestId("SelectField-Button-" + testId),
        onClick: ()=>setTouched(true)
    }, React__default.createElement(InputValueLabel, null, selectedLabel), React__default.createElement(StyledListBoxArrow, null, "\xa0"), React__default.createElement(InnerInputLabel, {
        "data-hpy-web-select-input-input-inner-label": true,
        size: "small"
    }, label)), React__default.createElement(SelectPopover, {
        portal: !isModal
    }, React__default.createElement(ListboxList, {
        "data-hpy-web-select-field-listbox-list": true
    }, children))), React__default.createElement(FieldErrorMessage, {
        name: name,
        reserveMessageSpace: reserveMessageSpace
    }));
};
DEPRECATED_SelectField.dataId = "data-hpy-web-select-field";
DEPRECATED_SelectField.displayName = "SelectField";

export { DEPRECATED_SelectField };
